/**
 * One table on the dashboard: its name, one number, and one small chart. The
 * whole card opens it.
 *
 * ONE NUMBER, NOT THREE. Each card showed three progress numbers, a date line,
 * a "Built in" badge, a row count and a spend link. The first number is the one
 * that matters (the server puts it first); the rest, where the card came from
 * and what it is missing are in its hover text. Filling gaps is the Brief's and
 * Research's job, not a link on every card.
 *
 * The numbers and chart come from the server (`glance`), off what the table
 * already worked out. A card with no glance - a note, a table the chat built -
 * shows its first few rows instead (`children`, drawn by the page). The × in
 * its corner takes it off the list; it waits in Add.
 */
import type { ReactNode } from 'react';
import type { BoardroomDeliverable } from '../../../types';
import { matchLine } from '../reports/tableFilter';
import { fillLine } from './DeliverableTile';
import GlanceChart from './GlanceChart';
import MadeByTag from './MadeByTag';
import { madeBy, subjectTag } from './provenance';

export default function GlanceCard({
  deliverable: d,
  badge,
  matches = null,
  onOpen,
  onRemove,
  title,
  origin,
  dragging = false,
  dropSide = null,
  glow = false,
  dragProps,
  children,
}: {
  deliverable: BoardroomDeliverable;
  /** How many items landed in it since this reader last looked. */
  badge?: number;
  matches?: { hits: number; seen: number } | null;
  onOpen: () => void;
  /** The card's name when it stands for a view ("Search"), not its one table. */
  title?: string;
  /** Take it off the list (the × in the corner). */
  onRemove?: () => void;
  /** Where it came from: "Filtered copy of Tools · from a noticing · Oct 5". */
  origin?: string;
  dragging?: boolean;
  /** A card being dragged will land on this side of this one: draw the bar. */
  dropSide?: 'before' | 'after' | null;
  /** Just added to the list: it glows for a few seconds so you can see where. */
  glow?: boolean;
  dragProps?: Record<string, unknown>;
  /** What the card shows when it has no glance. */
  children?: ReactNode;
}) {
  const g = d.glance;
  const empty = !d.fill.rows && !g?.stats.some(s => s.value);
  const lead = g?.stats[0];
  // Everything the face leaves out, on hover.
  const hover = [
    d.answers,
    g && g.stats.length > 1
      ? g.stats.map(s => `${s.value.toLocaleString()}${s.of !== undefined ? ` of ${s.of.toLocaleString()}` : ''} ${s.label}`).join(' · ')
      : '',
    fillLine(d),
    origin,
  ]
    .filter(Boolean)
    .join('\n');
  // Who made it, only when it is not one of the room's own tables.
  const made = madeBy(d);
  const showMade = !!made && made.label !== 'Built in';
  return (
    <div
      {...dragProps}
      data-just-added={glow || undefined}
      className={`group relative flex h-44 min-w-0 flex-col rounded-lg border ${glow ? 'jc-just-added ' : ''} border-gray-200 bg-white px-3 py-2.5 hover:border-indigo-300 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-indigo-600 ${
        dragging || (matches && !matches.hits) ? 'opacity-50' : ''
      }`}
      data-testid={`glance-card-${d.kind}`}
    >
      {/* IN THE GAP, centred: the grid's gap is 12px and the bar 4px. */}
      {dropSide && (
        <div
          className={`pointer-events-none absolute -bottom-px -top-px w-1 rounded-full bg-indigo-500 dark:bg-indigo-400 ${
            dropSide === 'before' ? '-left-2' : '-right-2'
          }`}
          data-testid={`drop-line-${d.id}`}
        />
      )}
      <button
        type="button"
        onClick={onOpen}
        title={hover}
        className="flex min-h-0 flex-1 flex-col text-left"
      >
        <div className={`flex items-start gap-1.5 ${onRemove ? 'pr-5' : ''}`}>
          <span className="line-clamp-1 min-w-0 flex-1 text-sm font-medium text-gray-900 dark:text-gray-100">
            {title ?? d.title}
          </span>
          {subjectTag(d) && (
            <span
              className="shrink-0 rounded bg-gray-100 px-1.5 text-[11px] text-gray-600 dark:bg-gray-700 dark:text-gray-300"
              data-testid={`subject-tag-${d.id}`}
            >
              {subjectTag(d)}
            </span>
          )}
          {d.lens?.tags?.length ? (
            <span className="shrink-0 text-[11px] text-indigo-600 dark:text-indigo-300">×{d.lens.tags.length}</span>
          ) : null}
          {!!badge && (
            <span className="shrink-0 rounded-full bg-indigo-100 px-1.5 text-[11px] text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200">
              +{badge}
            </span>
          )}
        </div>
        {matches && (
          <div className={`truncate text-xs ${matches.hits ? 'text-amber-700 dark:text-amber-300' : 'text-gray-400'}`}>
            {matchLine(matches)}
          </div>
        )}
        {lead && (
          <div className="mt-1.5 flex items-baseline gap-1.5" data-testid={`glance-stats-${d.kind}`}>
            <span className="text-2xl font-semibold leading-tight tabular-nums text-gray-900 dark:text-gray-100">
              {lead.value.toLocaleString()}
            </span>
            {lead.of !== undefined && (
              <span className="text-xs text-gray-400 dark:text-gray-500">of {lead.of.toLocaleString()}</span>
            )}
            <span className="text-xs text-gray-500 dark:text-gray-400">{lead.label}</span>
          </div>
        )}
        <div className="mt-2 min-h-0 flex-1 overflow-hidden">
          {g?.chart ? <GlanceChart chart={g.chart} /> : empty ? (
            <p className="text-xs text-gray-400 dark:text-gray-500">Nothing here yet.</p>
          ) : (
            children
          )}
        </div>
      </button>
      {/* OUTSIDE the card's own button: a button inside a button is not one. */}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          title="Unpin it from the Dashboard. It waits in All views."
          aria-label={`Unpin ${title ?? d.title} from the Dashboard`}
          className="absolute right-1.5 top-1.5 rounded px-1.5 text-sm leading-none text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200"
          data-testid={`glance-remove-${d.kind}`}
        >
          ×
        </button>
      )}
      {showMade && (
        <div className="mt-1 flex justify-end">
          <MadeByTag deliverable={d} title={origin} testId={`made-by-${d.id}`} />
        </div>
      )}
    </div>
  );
}
